import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { techSupportService } from '@/services/tech-support.service';
import { SearchInput } from '@/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

interface FilterValues {
  q?: string;
  status?: string;
  priority?: string;
  category_id?: string;
}

interface Props {
  values: FilterValues;
  onChange: (values: FilterValues) => void;
}

export function TicketFilters({ values, onChange }: Props) {
  const { data: categoriesData } = useQuery({
    queryKey: ['tech-support', 'categories'],
    queryFn: () => techSupportService.listCategories(),
  });
  const categories = categoriesData?.data?.data ?? [];

  const hasActiveFilters = Object.values(values).some(Boolean);
  const clear = () => onChange({ q: '', status: '', priority: '', category_id: '' });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SearchInput
        value={values.q ?? ''}
        onChange={(v) => onChange({ ...values, q: v })}
        placeholder="Search tickets…"
        containerClassName="min-w-[200px] sm:max-w-xs"
      />

      <Select value={values.status ?? ''} onValueChange={(v) => onChange({ ...values, status: v === '_all' ? '' : v })}>
        <SelectTrigger className="h-9 w-36">
          <SelectValue placeholder="All Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">All Status</SelectItem>
          <SelectItem value="open">Open</SelectItem>
          <SelectItem value="in_progress">In Progress</SelectItem>
          <SelectItem value="waiting">Waiting</SelectItem>
          <SelectItem value="resolved">Resolved</SelectItem>
          <SelectItem value="closed">Closed</SelectItem>
        </SelectContent>
      </Select>

      <Select value={values.priority ?? ''} onValueChange={(v) => onChange({ ...values, priority: v === '_all' ? '' : v })}>
        <SelectTrigger className="h-9 w-36">
          <SelectValue placeholder="All Priority" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">All Priority</SelectItem>
          <SelectItem value="low">Low</SelectItem>
          <SelectItem value="medium">Medium</SelectItem>
          <SelectItem value="high">High</SelectItem>
          <SelectItem value="urgent">Urgent</SelectItem>
        </SelectContent>
      </Select>

      {categories.length > 0 && (
        <Select value={values.category_id ?? ''} onValueChange={(v) => onChange({ ...values, category_id: v === '_all' ? '' : v })}>
          <SelectTrigger className="h-9 w-40">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={clear}>
          <X className="w-3.5 h-3.5 mr-1" />
          Clear
        </Button>
      )}
    </div>
  );
}
